import { HttpErrorResponse, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { catchError, throwError } from 'rxjs';
import { AppConfigService } from './appconfig.service';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private readonly msalService = inject(MsalService);
  private readonly config = inject(AppConfigService);
  private readonly router = inject(Router);
  private reloginInProgress = false;

  intercept(req: HttpRequest<any>, next: HttpHandler) {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 0) {
          console.error('[ErrorInterceptor] Network/CORS/SSL issue');
        } else if (error.status === 401) {
          console.error('[ErrorInterceptor] Unauthorized - token may be invalid or expired');
          this.reacquireTokenOrRelogin();
        } else if (error.status === 403) {
          console.error('[ErrorInterceptor] Forbidden - access denied');
          this.redirectToUnauthorized();
        }
        return throwError(() => error);
      }),
    );
  }

  /** Try to acquire a fresh token via MSAL first, if fails then clear storage and redirect to login */
  private reacquireTokenOrRelogin(): void {
    if (this.reloginInProgress) return;
    this.reloginInProgress = true;

    const scopes = [`${this.config.getConfig('clientId')}/.default`];
    const account = this.msalService.instance?.getActiveAccount();

    if (account) {
      this.msalService.instance.acquireTokenSilent({ scopes, account, forceRefresh: true })
        .then((result) => {
          sessionStorage.setItem('access_token', result.accessToken);
          if (result.expiresOn) {
            sessionStorage.setItem('access_token_expires_on', result.expiresOn.toISOString());
          }
          this.reloginInProgress = false;
        })
        .catch(() => {
          this.clearAllAndRelogin(scopes);
        });
    } else {
      this.clearAllAndRelogin(scopes);
    }
  }

  /** Clear all auth data and trigger MSAL login redirect */
  private clearAllAndRelogin(scopes: string[]): void {
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('access_token_expires_on');
    sessionStorage.removeItem('group_membership_verified');
    sessionStorage.removeItem('group_membership_group_id');

    try {
      this.msalService.loginRedirect({
        scopes,
        redirectStartPage: window.location.pathname,
      });
    } catch (err) {
      console.error('[ErrorInterceptor] Failed to trigger MSAL login redirect:', err);
      this.reloginInProgress = false;
    }
  }

  private redirectToUnauthorized(): void {
    if (!this.router.url.startsWith('/unauthorized')) {
      this.router.navigate(['/unauthorized']);
    }
  }
}